import { getConfig, setConfig } from '../../configs/config';
import getInitialData from '../../configs/initialData';
import { GoogleAnalyticsLimit } from '../../constants';
import messages from '../../constants/logMessages.json';
import logging, { getMessage } from '../logging/index';
import resolvePagePath from '../pagePath';
import tools from '../tools';

const {
  infoHeading,
  infoBold,
  infoRegular,
  warnHeading,
  warnBold,
  warnRegular,
  errorHeading,
  errorBold,
  errorRegular,
} = messages;

// Function to handle app completion logic
const handleAppCompletion = (data: any) => {
  const updatedData = { ...data };

  if (updatedData.appComplete) {
    const appStartTime =
      tools.handlePersistedConfig()?.analyticsTime?.appStartTime ?? null;

    if (appStartTime) {
      updatedData.timeAppCompletion =
        tools.getTimeElapsedInSeconds?.(appStartTime);
    } else {
      logging.warn(getMessage('appStartTimeNotFound'), warnHeading, warnBold);
    }
  }

  return updatedData;
};

export const truncate = (
  str: string | undefined,
  paraType: GoogleAnalyticsLimit
) => {
  if (!str) return str;
  return str.length > paraType ? `${str.substring(0, paraType - 1)}...` : str;
};

export const omitBy = (obj: object) =>
  Object.fromEntries(
    Object.entries(obj).filter(
      ([_, v]) => v !== '' && v !== null && v !== undefined
    )
  );

export const isEmpty = (obj: object | null | undefined) =>
  !obj || Object.keys(obj).length === 0;

export const mergeInitialData = (payload: any) => {
  const initialData = getInitialData();
  const updatedData = { ...payload.data, ...initialData };
  const finalData = handleAppCompletion(updatedData);

  return {
    ...payload,
    data: finalData,
  };
};

export const getProperty = (obj: unknown, key: string): unknown =>
  obj && typeof obj === 'object' && key in obj ? (obj as any)[key] : undefined;

// Checks the qbeTrack.meta setting first for a boolean, then falls back to the wrapperSetting
export const getBoolPref = (
  metaDataSetting: boolean | undefined,
  wrapperSetting: boolean
) => (typeof metaDataSetting === 'boolean' ? metaDataSetting : wrapperSetting);

// Checks the qbeTrack.meta setting first for a non-empty Array<string>, then falls back to the wrapperSetting
export const getArrPref = (
  metaDataSetting: Array<string> | undefined,
  wrapperSetting: Array<string>
) =>
  metaDataSetting && !isEmpty(metaDataSetting)
    ? metaDataSetting
    : wrapperSetting;

// Checks the qbeTrack.meta setting first for a boolean or a non-empty Array<string>, then falls back to the wrapperSetting
export const getBoolArrPref = (
  metaDataSetting: Array<string> | boolean | undefined,
  wrapperSetting: Array<string> | boolean
) =>
  typeof metaDataSetting === 'boolean' ||
  (metaDataSetting && !isEmpty(metaDataSetting))
    ? metaDataSetting
    : wrapperSetting;

// Checks the qbeTrack.meta setting first for a boolean or a non-empty object, then falls back to the wrapperSetting
export const getBoolObjPref = (
  metaDataSetting: object | boolean | undefined,
  wrapperSetting: object | boolean
) =>
  typeof metaDataSetting === 'boolean' ||
  (metaDataSetting && !isEmpty(metaDataSetting))
    ? metaDataSetting
    : wrapperSetting;

/**
 * Retrieves the value of a query parameter from the current URL.
 * @param param The name of the query parameter.
 * @returns The value of the query parameter if found, otherwise null.
 */
export const getQueryParameter = (param: string): string | null => {
  if (!param) return null;

  // Ensure the parameter name is safe and does not contain any special characters
  const safeParam = encodeURIComponent(param);

  const urlParams = new URLSearchParams(window.location.search);
  const value = urlParams.get(safeParam);

  // Basic sanitisation: ensure the returned value is a safe string
  return value ? value.replace(/[<>"'`;&]/g, '') : null;
};

/**
 * Retrieves the page referrer from configuration or document.referrer.
 * Falls back to checking for a 'referrer' query parameter in the URL if not found.
 * @returns The process page referrer.
 */
export const getPageReferrer = (): string => {
  const { pageReferrer } = getConfig(); // Assuming getConfig() retrieves configuration

  if (!pageReferrer)
    return document.referrer || getQueryParameter('referrer') || '';

  return pageReferrer;
};

/**
 * Sets the page referrer in the application configuration based on the current page.
 * @param pageName The name or identifier of the current page.
 */
export const setPageReferrer = (pageName: string): void => {
  // Get the protocol (http: or https:)
  const { protocol } = window.location;

  // Get the hostname (e.g., www.abc.com)
  const { hostname } = window.location;

  // Resolve the full path of the current page based on its name or identifier
  const pagePath = resolvePagePath(pageName); // Assuming resolvePagePath() resolves the page path

  // Combine protocol and hostname to form the full host name (e.g., http://www.abc.com)
  const fullHostName = `${protocol}//${hostname}`;

  // Set the page referrer and previous page path in the application configuration
  setConfig({
    pageReferrer: `${fullHostName}${pagePath}`,
    previousPagePath: pagePath,
  });
};

export const getTargetOriginUrl = (param: string): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  const value = urlParams.get(param);

  if (!value) {
    logging.info(
      getMessage('targetOriginByUrlEmpty'),
      infoHeading,
      infoRegular,
      infoBold
    );
    return null;
  }

  try {
    const url = new URL(value);
    // Allow only http and https protocols to avoid Javascript injection
    if (url.protocol.startsWith('http')) return url.origin;

    // Log when targetOrigin doesn't have a valid protocol
    logging.error(getMessage('targetOriginUrlError'), errorHeading, errorBold);
    return null;
  } catch (e) {
    const err = e instanceof Error ? `${e.name}: ${e.message}` : String(e);
    logging.error(
      getMessage('targetOriginUrlParsingError', [err]),
      errorHeading,
      errorRegular,
      errorBold
    );
    return null;
  }
};

type TargetOriginName = {
  name?: string;
  targetOrigin: string;
};

export const getTargetOriginName = (jsonString: string): string | null => {
  try {
    if (!jsonString) {
      logging.warn(
        getMessage('targetOriginByNameEmpty'),
        warnHeading,
        warnRegular,
        warnBold
      );
      return null;
    }
    const parsed = JSON.parse(jsonString) as TargetOriginName;
    const rawOrigin = parsed.targetOrigin;
    if (!rawOrigin) return null;

    const targetOrigin = decodeURIComponent(rawOrigin);

    // Allow only http and https protocols to avoid Javascript injection
    if (!targetOrigin.startsWith('http')) {
      logging.error(
        getMessage('targetOriginUrlError'),
        errorHeading,
        errorBold
      );
      return null;
    }

    return targetOrigin;
  } catch (e) {
    const err = e instanceof Error ? `${e.name}: ${e.message}` : String(e);
    logging.error(
      getMessage('targetOriginJsonError', [err]),
      errorHeading,
      errorRegular,
      errorBold
    );
    return null;
  }
};

// isStandalone = true means the MFE has been launched from within an iFrame.
export const isStandalone: boolean = window.self === window.top;
